import { useTranslations } from '@aikoa/i18n/react'
import { observable, ObservableHint } from '@legendapp/state'
import { observer, use$ } from '@legendapp/state/react'
import { Link, usePathname, useRouter } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, { LinearTransition, SlideInLeft, SlideInRight } from 'react-native-reanimated'
import { AppMenu } from '~/components/AppMenu.tsx'
import Icon from '~/components/Icon.tsx'
import { Badge } from '~/components/ui/badge'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { useSession } from '~/components/user/SessionProvider'
import { UserButton } from '~/components/UserButton.tsx'
import { useUserProfile } from '~/data/user/userProfile'
import designTokens from '~/lib/designTokens.ts'
import { cn } from '~/lib/utils.ts'
import { LanguageSelector } from './LanguageSelector'

export type AppHeaderStates = 'login' | 'back' | 'menu' | 'user'

export const appHeaderState$ = observable({
  restrictedStates: new Set<AppHeaderStates>(),
  backAction: ObservableHint.opaque<{
    onBack: (() => unknown) | null
  }>({
    onBack: null,
  }),
  canGoBack: false,
  setBackAction: (action: (() => unknown) | null) => {
    appHeaderState$.backAction.set(
      ObservableHint.opaque({
        onBack: action,
      })
    )
  },
})

const AppHeader = observer(function AppHeader({
  className,
  showMenuButton = true,
  showBackButton: showBackButtonProp = true,
  showLoginButton: showLoginButtonProp = true,
  logoIsLink = true,
}: {
  className?: string
  showMenuButton?: boolean
  showBackButton?: boolean
  showLoginButton?: boolean
  logoIsLink?: boolean
}) {
  const { t } = useTranslations('common')
  const { canGoBack: _canGoBack, backAction } = use$(appHeaderState$)

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { session } = useSession()

  const pathname = usePathname()
  const router = useRouter()

  const canGoBack = _canGoBack || !!backAction?.onBack

  useEffect(() => {
    appHeaderState$.canGoBack.set(
      (pathname !== '/' && router.canGoBack()) || backAction.onBack !== null
    )
  }, [router, pathname, backAction])

  const onBackPress = useCallback(() => {
    if (backAction.onBack) {
      backAction.onBack()
    } else if (_canGoBack) {
      router.back()
    } else {
      router.replace(session ? '/(authenticated)' : '/')
    }

    setIsMenuOpen(false)
  }, [backAction, _canGoBack, router, session])

  const showBackButton =
    showBackButtonProp &&
    (!['/'].includes(pathname) || canGoBack) &&
    !appHeaderState$.restrictedStates.has('back')

  const { data: profile } = useUserProfile()
  const isRealtor = profile?.roles?.includes('realtor') || false

  const showUserButton = !showBackButton && !!session && isRealtor
  const showLoginButton = showLoginButtonProp && !showUserButton && !session

  const logo = (
    <View className="relative">
      <Icon name="aikoa" width={96.294} height={25.569} color={designTokens.tertiaryForeground} />
      <Badge
        variant="default"
        className="absolute -bottom-3 -right-3"
        textClassName="text-tertiary-foreground! text-xxxs"
        size="xxs">
        BETA
      </Badge>
    </View>
  )

  return (
    <View className={'z-9999 relative isolate mx-2 h-[73px] max-w-[calc(100%-1rem)]'}>
      <View
        className={cn('relative mx-auto w-full max-w-xl rounded-content bg-secondary', className)}>
        <View className="h-[73px] flex-row items-center justify-between overflow-hidden px-2 xs:px-4">
          <Animated.View
            key={`${showBackButton ? 'back' : showUserButton ? 'user' : 'login'}-button`}
            entering={SlideInLeft.springify().damping(37).stiffness(450)}
            className={cn('flex w-32 shrink-0 flex-row items-center gap-3.5 pl-0')}>
            {showBackButton ? (
              <Button
                variant="outline"
                size="icon"
                onPress={onBackPress}
                className="group size-[47px] border-tertiary-foreground active:border-accent">
                <Icon
                  name="arrow-left"
                  size={23}
                  library="feather"
                  className="text-tertiary-foreground group-hover:text-accent group-active:text-accent"
                />
              </Button>
            ) : showUserButton ? (
              <Link href="/(authenticated)/profile" className="group py-4 xs:-ml-1">
                <UserButton
                  showName={false}
                  className="gap-2 p-0 xs:gap-4"
                  avatarClassName="border-none size-8 xs:size-10"
                />
              </Link>
            ) : showLoginButton ? (
              pathname !== '/login' &&
              !appHeaderState$.restrictedStates.has('login') && (
                <LinkButton
                  variant="ghost"
                  size="sm"
                  href="/login"
                  className={cn(
                    'web:hover:bg-transparent flex-row items-center justify-end gap-3.5 px-0 py-2.5 pl-4'
                  )}>
                  <Icon
                    name="login"
                    size={16}
                    className="text-tertiary-foreground group-hover/button:text-accent group-active/button:text-accent"
                  />
                  <ButtonLabel className="text-lg/loose text-tertiary-foreground group-hover/button:text-accent group-active/button:text-accent">
                    {t(($) => $.header.login)}
                  </ButtonLabel>
                </LinkButton>
              )
            ) : null}
          </Animated.View>
          <Animated.View
            layout={LinearTransition.springify().damping(40).mass(2).stiffness(500)}
            className={cn('flex-1')}>
            {isMenuOpen || !logoIsLink ? (
              <Pressable
                onPress={() => setIsMenuOpen(false)}
                className="flex-1 items-center justify-center">
                {logo}
              </Pressable>
            ) : (
              <Link href="/" className="flex flex-1 items-center justify-center">
                {logo}
              </Link>
            )}
          </Animated.View>
          <Animated.View
            className="flex w-32 shrink-0 flex-row items-center justify-end gap-3.5"
            entering={SlideInRight.springify().damping(37).stiffness(450)}
            key={`${session && showMenuButton && isRealtor ? 'menu' : 'language'}-menu`}>
            {session &&
            showMenuButton &&
            isRealtor &&
            !appHeaderState$.restrictedStates.has('menu') ? (
              <Pressable
                onPress={() => setIsMenuOpen((current) => !current)}
                className={cn('group/button flex-row items-center justify-end gap-3.5 py-4')}>
                <ButtonLabel className="text-lg/loose text-tertiary-foreground transition-colors duration-150 group-hover/button:text-accent group-active/button:text-accent">
                  {t(($) => $.header.menu)}
                </ButtonLabel>
                <Icon
                  name="menu"
                  size={20}
                  library="feather"
                  className="text-tertiary-foreground transition-colors duration-150 group-hover/button:text-accent group-active/button:text-accent"
                />
              </Pressable>
            ) : (
              <LanguageSelector />
            )}
          </Animated.View>
        </View>
        <AppMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      </View>
    </View>
  )
})

export default AppHeader
